import { cn } from "@newxsystems/shared";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITextContentGridBlockProps } from "./TextContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;
export const TextContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  headingEffect,
  items,
  parentClass,
  spacing,
}: ITextContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          headingEffect={headingEffect}
          reveal
        />
        <div
          className={cn(
            "grid grid-cols-1 border-t border-l border-border sm:grid-cols-2",
            columns[columnCount || 3],
          )}
          data-reveal="stagger"
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "group/card relative flex min-h-72 flex-col gap-12 border-r border-b border-border p-4 transition-colors duration-200 hover:bg-surface-100 md:p-6",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span
                aria-hidden="true"
                className="absolute inset-y-0 left-0 w-px origin-top scale-y-0 bg-primary transition-transform duration-300 ease-out group-hover/card:scale-y-100"
              />
              <div className="flex items-start justify-between gap-6">
                <span className="signal-numeric text-xs text-primary">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="font-daylight-body text-lg leading-none text-ash transition-[transform,color] duration-200 ease-out group-hover/card:translate-x-1 group-hover/card:text-primary"
                >
                  →
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-4">
                <h3 className="max-w-[17ch] font-daylight-display text-2xl font-semibold leading-[1.1] tracking-[-0.02em] text-foreground md:text-[1.75rem]">
                  {item.heading}
                </h3>
                {item.description ? (
                  <DaylightRichText
                    className="max-w-[40ch] text-base text-ash"
                    content={item.description}
                  />
                ) : null}
                {item.cardLink?.label ? (
                  <span className="signal-label mt-auto pt-1 text-primary">
                    {item.cardLink.label}
                  </span>
                ) : null}
              </div>
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TextContentGridBlock.displayName = "Block::TextContentGridBlock";
